import { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth, Roles } from "../../providers/AuthContext";
import { postInitiateHandshake } from "../../api/RemotePartiesOCPI";
import toast from "react-hot-toast";
import LoadingTablePage from "../LoadingTablePage";
import HandshakePartyForm from "../../components/ocpiParty/HandshakePartyForm";
import { getAllCustomers } from "../../api/Customers";
import { getCustomerDisplayName } from "../../helpers/CustomerDisplay";

const initialForm = () => ({
    remotePartyName: "",
    emspDecodedTokenA: "",
    emspVersionsUrl: "",
    versionNumber: "2.2.1",
    identifiers: ["credentials"],
    customerPublicId: "",
});

const HandshakeOcpiPartyPage = () => {
    const { t } = useTranslation();
    const { getAccessToken, hasRole } = useAuth();
    const navigate = useNavigate();
    const [loading] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [invalidFields, setInvalidFields] = useState([]);
    const [form, setForm] = useState(initialForm);
    const [customers, setCustomers] = useState([]);

    useEffect(() => {
        const handleGetCustomers = async () => {
            try {
                const accessToken = await getAccessToken();
                const newCustomers = await getAllCustomers(accessToken);
                setCustomers(
                    (newCustomers || []).map((customer) => ({
                        ...customer,
                        displayName: getCustomerDisplayName(customer, hasRole(Roles.Admin)),
                    }))
                );
            } catch (error) {
                console.error(error);
            }
        };
        if (customers.length === 0) {
            handleGetCustomers();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
    }, []);

    const handleFieldChange = useCallback((key, value) => {
        setHasChanges(true);
        setForm((prev) => ({ ...prev, [key]: value }));
    }, []);

    const handleVersionChange = useCallback((version) => {
        setHasChanges(true);
        setForm((prev) => {
            const next = { ...prev, versionNumber: version ?? "2.2.1" };
            if (next.versionNumber === "2.2.1") {
                next.identifiers = prev.identifiers.filter((id) => id !== "Booking");
            }
            return next;
        });
    }, []);

    const validate = () => {
        const missing = [];

        if (!form.remotePartyName.trim()) {
            missing.push({ key: "remotePartyName", value: t("common.name") });
        }
        if (!form.emspDecodedTokenA.trim()) {
            missing.push({ key: "emspDecodedTokenA", value: t("form.decodedTokenA") });
        }
        if (!form.emspVersionsUrl.trim()) {
            missing.push({ key: "emspVersionsUrl", value: t("form.emspVersionsUrl") });
        }
        if (!form.versionNumber) {
            missing.push({ key: "versionNumber", value: t("form.version") });
        }
        if (!form.identifiers || form.identifiers.length === 0) {
            missing.push({ key: "identifiers", value: t("form.ocpiModules") });
        }
        if (
            (form.identifiers.includes("cdrs") ||
                form.identifiers.includes("sessions") ||
                form.identifiers.includes("Booking")) &&
            (form.customerPublicId === "" || form.customerPublicId == null)
        ) {
            missing.push({ key: "customerPublicId", value: t("entity.customer") });
        }

        return missing;
    };

    const handleHandshake = async () => {
        const missing = validate();
        if (missing.length > 0) {
            setInvalidFields(missing.map((item) => item.key));
            const message = t("common.pleaseFillRequiredFields", { fields: missing.map((obj) => obj.value).join(", ") });
            toast.error(message);
            return;
        }

        setInvalidFields([]);
        const toastId = toast.loading(t("ocpiParty.initiatingHandshake"));

        try {
            const accessToken = await getAccessToken();
            const payload = {
                remotePartyName: form.remotePartyName.trim(),
                emspDecodedTokenA: form.emspDecodedTokenA.trim(),
                emspVersionsUrl: form.emspVersionsUrl.trim(),
                versionNumber: form.versionNumber,
                identifiers: form.identifiers,
            };
            if (form.customerPublicId) {
                payload.customerPublicId = form.customerPublicId;
            }

            const result = await postInitiateHandshake(payload, accessToken);
            toast.success(t("ocpiParty.handshakeInitiated"), { id: toastId });

            if (result?.remotePartyPublicId) {
                navigate(`/ocpi-party/${result.remotePartyPublicId}`);
            } else {
                navigate("/ocpi-parties");
            }
        } catch (error) {
            toast.error(error?.message || t("ocpiParty.failedHandshake"), { id: toastId });
        }
    };

    const navigateBack = () => {
        if (navigate.length > 1) {
            navigate(-1);
        } else {
            navigate("/ocpi-parties");
        }
    };

    return (
        <>
            <HeaderBar />
            <div className="flex flex-row w-full">
                <Sidebar />
                <main className="page">
                    <div className="absolute inset-0 pointer-events-none">
                        <LoadingTablePage title={t("nav.ocpiParties")} loading={loading} />
                    </div>

                    <div className={`${loading ? "loading" : ""} page-content`}>
                        <div className="flex flex-row items-center pb-4">
                            <button
                                onClick={() => navigateBack()}
                                className="text-black flex"
                            >
                                <FontAwesomeIcon icon={faArrowLeft} className="w-4 pr-4" />
                            </button>
                            <h2 className="border-l border-mute-200 pl-4 ">{t("pages.ocpiHandshake")}</h2>
                            <div className="flex gap-4 ml-auto">
                                <button
                                    className="btn btn-outline-primary"
                                    onClick={() => navigateBack()}
                                >
                                    {t("common.cancel")}
                                </button>
                                <button
                                    className="btn btn-primary"
                                    disabled={!hasChanges}
                                    onClick={handleHandshake}
                                >
                                    {t("pages.handshake")}
                                </button>
                            </div>
                        </div>

                        <div className="px-4 py-8 shadow-lg rounded-md w-full">
                            <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-4 items-start">
                                <HandshakePartyForm
                                    form={form}
                                    onFieldChange={handleFieldChange}
                                    onVersionChange={handleVersionChange}
                                    customers={customers}
                                    invalidFields={invalidFields}
                                />
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </>
    );
};

export default HandshakeOcpiPartyPage;
